import type { ReactNode } from "react";
import { PointIcon } from "../icon";
import { boundPoint, Frame, key, Notes, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

/**
 * What the statistics widgets share (pro-statistics PST-04, PST-13, PST-30): the bound value and its answer, and the
 * frames for a plan without statistics, a period outside the plan's history and no readings yet. `body` draws the
 * answer; it gets the offline line to put at its bottom.
 */
export function StatisticsWidget({
  props,
  body,
}: {
  props: WidgetProps;
  body: (args: {
    answer: NonNullable<NonNullable<WidgetProps["input"]["statistics"]>[string]>;
    point: NonNullable<NonNullable<WidgetProps["input"]["points"]>[string]>;
    name: string;
    icon: ReactNode;
    footer: ReactNode;
    offline: boolean;
  }) => ReactNode;
}) {
  const { widget, input, now, onSeePlans } = props;
  const fmt = useWidgetText();
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const name = widget.title ?? point?.name ?? "";
  const answer = bound?.binding.datapoint
    ? input.statistics?.[key(bound.binding.device, bound.binding.datapoint)]
    : undefined;
  const icon = <PointIcon point={point} />;
  const problem = answer?.problem;
  if (problem?.code === "plan_feature") {
    const locked = fmt.t("statistics.locked");
    return (
      <Frame widget={widget} state="locked" title={name} icon={icon}>
        <p aria-label={`${name}: ${locked}`} className="font-medium text-gray-900">
          {locked}
        </p>
        {input.can_change_plan ? (
          <div className="mt-auto">
            <button
              type="button"
              onClick={onSeePlans}
              className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
              {fmt.t("statistics.seePlans")}
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-700">{fmt.t("statistics.askAdmin")}</p>
        )}
      </Frame>
    );
  }
  if (problem?.code === "outside_history") {
    const text = fmt.t("statistics.outsideHistory");
    return (
      <Frame widget={widget} state="outside_history" title={name} icon={icon}>
        <p aria-label={`${name}: ${text}`} className="text-gray-700">
          {text}
        </p>
      </Frame>
    );
  }
  const hasData =
    (answer?.periods ?? []).some((p) => p.value !== null) ||
    (answer?.months ?? []).some((y) => y.months.some((m) => m.value !== null));
  if (!point || !answer || !hasData) {
    return (
      <Frame widget={widget} state="empty" title={name} icon={icon}>
        <p aria-label={`${name}: ${fmt.t("state.noData")}`} className="text-gray-700">
          {fmt.t("state.noData")}
        </p>
      </Frame>
    );
  }
  const offline = bound.device?.state === "offline";
  const seen = bound.device?.last_seen_at;
  const footer =
    offline && seen ? <Notes items={[{ text: fmt.t("statistics.offline", { age: fmt.age(seen, now) }) }]} /> : null;
  return body({ answer, point, name, icon, footer, offline });
}
